/**
 * Utilities for handling authentication in iframe contexts
 * Implements message passing between iframe and parent window for access tokens
 */

export interface TokenRequest {
  type: "AUTH_TOKEN_REQUEST";
  requestId: string;
}

export interface TokenResponse {
  type: "AUTH_TOKEN_RESPONSE";
  requestId: string;
  token: string | null;
  error?: string;
}

export type AuthMessage = TokenRequest | TokenResponse;

/**
 * Check if current window is in an iframe
 */
export const isInIframe = (): boolean => {
  try {
    return window.self !== window.top;
  } catch (_e) {
    // If we can't access window.top due to cross-origin restrictions,
    // we're likely in an iframe
    return true;
  }
};

/**
 * Request access token from parent window (for use in iframe)
 */
export const requestTokenFromParent = (): Promise<string | null> => {
  return new Promise((resolve, reject) => {
    if (!isInIframe()) {
      reject(new Error("Not in iframe context"));
      return;
    }

    const requestId = `auth_request_${Date.now()}_${Math.random()}`;
    const timeout = 10000; // 10 seconds timeout

    const handleMessage = (event: MessageEvent<AuthMessage>) => {
      // Basic security check - ensure we're only responding to our own requests
      if (
        event.data?.type === "AUTH_TOKEN_RESPONSE" &&
        event.data.requestId === requestId
      ) {
        window.removeEventListener("message", handleMessage);
        clearTimeout(timeoutId);

        if (event.data.error) {
          reject(new Error(event.data.error));
        } else {
          resolve(event.data.token);
        }
      }
    };

    const timeoutId = setTimeout(() => {
      window.removeEventListener("message", handleMessage);
      reject(new Error("Token request timeout"));
    }, timeout);

    window.addEventListener("message", handleMessage);

    // Send request to parent
    const request: TokenRequest = {
      type: "AUTH_TOKEN_REQUEST",
      requestId,
    };

    window.parent.postMessage(request, "*");
  });
};

/**
 * Set up token request handler in parent window
 */
export const setupTokenRequestHandler = (
  getToken: () => Promise<string | null>,
): (() => void) => {
  const handleMessage = async (event: MessageEvent<AuthMessage>) => {
    // Only handle token requests
    if (event.data?.type === "AUTH_TOKEN_REQUEST") {
      try {
        const token = await getToken();
        const response: TokenResponse = {
          type: "AUTH_TOKEN_RESPONSE",
          requestId: event.data.requestId,
          token,
        };
        event.source?.postMessage(response, { targetOrigin: event.origin });
      } catch (error) {
        const response: TokenResponse = {
          type: "AUTH_TOKEN_RESPONSE",
          requestId: event.data.requestId,
          token: null,
          error: error instanceof Error ? error.message : "Unknown error",
        };
        event.source?.postMessage(response, { targetOrigin: event.origin });
      }
    }
  };

  window.addEventListener("message", handleMessage);

  // Return cleanup function
  return () => {
    window.removeEventListener("message", handleMessage);
  };
};
